import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  ImageList,
  ImageListItem,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  Note as NoteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  CompareArrows as CompareArrowsIcon,
  ArrowForward as ArrowForwardIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { VehicleConditionAssessment } from '../../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface CompareAssessmentsModalProps {
  assessment1: VehicleConditionAssessment;
  assessment2: VehicleConditionAssessment;
  open: boolean;
  onClose: () => void;
  onEdit?: (assessment: VehicleConditionAssessment) => void;
}

const CompareAssessmentsModal: React.FC<CompareAssessmentsModalProps> = ({
  assessment1,
  assessment2,
  open,
  onClose,
  onEdit
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [selectedPhotoMode, setSelectedPhotoMode] = useState(false);
  const [selectedPhotos1, setSelectedPhotos1] = useState<number[]>([]);
  const [selectedPhotos2, setSelectedPhotos2] = useState<number[]>([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  const handleImageClick = (imageUrl: string) => {
    if (!selectedPhotoMode) {
      setLightboxImage(imageUrl);
      setLightboxOpen(true);
    }
  };

  const handlePhotoSelect = (assessmentIndex: 1 | 2, photoIndex: number) => {
    if (assessmentIndex === 1) {
      setSelectedPhotos1(prev => 
        prev.includes(photoIndex) 
          ? prev.filter(i => i !== photoIndex)
          : [...prev, photoIndex]
      );
    } else {
      setSelectedPhotos2(prev => 
        prev.includes(photoIndex) 
          ? prev.filter(i => i !== photoIndex)
          : [...prev, photoIndex]
      );
    }
  };

  const togglePhotoSelectionMode = () => {
    setSelectedPhotoMode(!selectedPhotoMode);
    if (selectedPhotoMode) {
      setSelectedPhotos1([]);
      setSelectedPhotos2([]);
    }
  };

  const handleCopyDescriptions = () => {
    // Clear any previous errors
    setCopyError(null);
    
    // Validate: Ensure assessment2 has photos to copy descriptions to
    if (!assessment2.photos || assessment2.photos.length === 0) {
      setCopyError('L\'évaluation actuelle n\'a pas de photos. Veuillez d\'abord ajouter des photos avant de copier les descriptions.');
      return;
    }

    // Validate: Ensure assessment1 has photos or notes to copy from
    const hasPhotosToCopy = assessment1.photos && assessment1.photos.length > 0;
    const hasNotesToCopy = assessment1.notes && assessment1.notes.length > 0;
    
    if (!hasPhotosToCopy && !hasNotesToCopy) {
      setCopyError('L\'évaluation précédente n\'a pas de descriptions ou de notes à copier.');
      return;
    }

    // Smart photo matching: prioritize by type, then by index
    const updatedPhotos = assessment2.photos.map((photo, index) => {
      // First, try to find a photo with the same type in assessment1
      let matchingPhoto = assessment1.photos?.find(p => p.type === photo.type);
      
      // If no match by type, try by index (for same position)
      if (!matchingPhoto && assessment1.photos?.[index]) {
        matchingPhoto = assessment1.photos[index];
      }
      
      // Only copy description if it exists and is not empty
      if (matchingPhoto?.description && matchingPhoto.description.trim()) {
        return {
          ...photo,
          description: matchingPhoto.description.trim()
        };
      }
      
      // Keep existing description if no match found
      return photo;
    });

    // Copy notes from assessment1 to assessment2
    // Only copy notes that don't already exist (avoid duplicates)
    const existingNoteContents = new Set(
      (assessment2.notes || []).map(note => note.content.trim().toLowerCase())
    );
    
    const copiedNotes = (assessment1.notes || [])
      .filter(note => {
        // Only copy if content doesn't already exist and is not empty
        const noteContent = note.content.trim().toLowerCase();
        return noteContent && !existingNoteContents.has(noteContent);
      })
      .map(note => ({
        ...note,
        id: 0, // New note, will be created
        assessment_id: assessment2.id,
        created_by: 0, // Will be set by backend
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

    // Count how many descriptions and notes were copied
    const photosCopied = updatedPhotos.filter((photo, index) => {
      const originalPhoto = assessment2.photos?.[index];
      return originalPhoto && photo.description !== originalPhoto.description;
    }).length;
    
    const notesCopied = copiedNotes.length;

    // Check if anything was actually copied
    if (photosCopied === 0 && notesCopied === 0) {
      setCopyError('Aucune description ou note n\'a pu être copiée. Toutes les descriptions existent déjà ou l\'évaluation précédente n\'a pas de contenu à copier.');
      return;
    }

    // Create updated assessment with copied descriptions and notes
    const updatedAssessment: VehicleConditionAssessment = {
      ...assessment2,
      photos: updatedPhotos,
      notes: [...(assessment2.notes || []), ...copiedNotes]
    };

    // Show success message
    setCopySuccess(true);

    // Trigger edit with updated assessment
    if (onEdit) {
      setTimeout(() => {
        onEdit(updatedAssessment);
        onClose();
      }, 800); // Slightly longer delay to show feedback
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return '#4caf50';
      case 'fair': return '#8bc34a';
      case 'poor': return '#ff9800';
      case 'critical': return '#f44336';
      default: return '#2196f3';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'good': return 'Bon';
      case 'fair': return 'Correct';
      case 'poor': return 'Mauvais';
      case 'critical': return 'Critique';
      default: return 'Inconnu';
    }
  };

  const getStatusValue = (status: string): number => {
    switch (status) {
      case 'good': return 4;
      case 'fair': return 3;
      case 'poor': return 2;
      case 'critical': return 1;
      default: return 0;
    }
  };

  const getTrendIcon = () => {
    const value1 = getStatusValue(assessment1.status);
    const value2 = getStatusValue(assessment2.status);
    
    if (value2 > value1) return <TrendingUpIcon sx={{ color: '#4caf50' }} />;
    if (value2 < value1) return <TrendingDownIcon sx={{ color: '#f44336' }} />;
    return <TrendingFlatIcon sx={{ color: '#9e9e9e' }} />;
  };

  const renderAssessmentCard = (assessment: VehicleConditionAssessment, title: string) => (
    <Card sx={{ flex: 1, minWidth: 0 }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {format(new Date(assessment.assessment_date), 'dd MMMM yyyy', { locale: fr })}
          </Typography>
          <Chip
            label={getStatusLabel(assessment.status)}
            size="small"
            sx={{ 
              backgroundColor: getStatusColor(assessment.status),
              color: 'white'
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Statistics */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhotoCameraIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {assessment.photos?.length || 0} photos
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <NoteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {assessment.notes?.length || 0} notes
            </Typography>
          </Box>
        </Box>

        {/* Photos */}
        {assessment.photos && assessment.photos.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Photos ({assessment.photos.length}):
            </Typography>
            <ImageList 
              cols={isMobile ? 1 : 2} 
              gap={isMobile ? 4 : 8} 
              sx={{ maxHeight: selectedPhotoMode ? (isMobile ? 400 : 300) : (isMobile ? 250 : 200) }}
            >
              {assessment.photos.map((photo, index) => {
                const isSelected = title === 'État précédent' 
                  ? selectedPhotos1.includes(index)
                  : selectedPhotos2.includes(index);
                
                return (
                  <ImageListItem 
                    key={index}
                    sx={{ 
                      position: 'relative',
                      cursor: selectedPhotoMode ? 'pointer' : 'default'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        border: isSelected ? '3px solid #2196f3' : '1px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: selectedPhotoMode ? 'scale(1.02)' : 'scale(1.05)',
                          boxShadow: isSelected ? '0 4px 12px rgba(33, 150, 243, 0.4)' : '0 2px 8px rgba(0,0,0,0.2)'
                        }
                      }}
                      onClick={() => {
                        if (selectedPhotoMode) {
                          handlePhotoSelect(title === 'État précédent' ? 1 : 2, index);
                        } else {
                          handleImageClick(photo.file_url || '');
                        }
                      }}
                    >
                      <img
                        src={photo.file_url || `/storage/vehicle-assessments/${photo.file_path}`}
                        alt={photo.description || `Photo ${index + 1}`}
                        loading="lazy"
                        style={{ 
                          height: selectedPhotoMode 
                            ? (isMobile ? 200 : 120) 
                            : (isMobile ? 150 : 80), 
                          width: '100%',
                          objectFit: 'cover',
                          opacity: isSelected ? 1 : selectedPhotoMode ? 0.7 : 1
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', photo);
                          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23ddd" width="80" height="80"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {selectedPhotoMode && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: { xs: 8, md: 4 },
                            right: { xs: 8, md: 4 },
                            bgcolor: isSelected ? '#2196f3' : 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '50%',
                            p: { xs: 1, md: 0.5 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: { xs: 40, md: 32 },
                            minHeight: { xs: 40, md: 32 }
                          }}
                        >
                          {isSelected ? (
                            <CheckCircleIcon sx={{ color: 'white', fontSize: { xs: 28, md: 20 } }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ color: 'white', fontSize: { xs: 28, md: 20 } }} />
                          )}
                        </Box>
                      )}
                      {photo.description && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            p: { xs: 1, md: 0.5 },
                            fontSize: { xs: '0.75rem', md: '0.7rem' }
                          }}
                        >
                          {photo.description}
                        </Box>
                      )}
                    </Box>
                  </ImageListItem>
                );
              })}
            </ImageList>
          </Box>
        )}

        {/* Notes */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Notes:
          </Typography>
          {assessment.general_notes && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#2196f3' }}>📝 Général:</Typography>
              <Typography variant="body2" color="text.secondary">
                {assessment.general_notes}
              </Typography>
            </Box>
          )}
          {assessment.damage_notes && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#f44336' }}>🔧 Dommages:</Typography>
              <Typography variant="body2" color="text.secondary">
                {assessment.damage_notes}
              </Typography>
            </Box>
          )}
          {assessment.alert_notes && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#ff9800' }}>⚠️ Alertes:</Typography>
              <Typography variant="body2" color="text.secondary">
                {assessment.alert_notes}
              </Typography>
            </Box>
          )}
          {assessment.notes && assessment.notes.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Notes structurées:</Typography>
              <List dense>
                {assessment.notes.map((note, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={note.content}
                      secondary={note.type}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {!assessment.general_notes && !assessment.damage_notes && !assessment.alert_notes && (!assessment.notes || assessment.notes.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Aucune note
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          borderRadius: { xs: 0, md: 3 }, 
          m: { xs: 0, md: 2 }, 
          height: { xs: '100vh', md: '90vh' },
          maxHeight: { xs: '100vh', md: '90vh' }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            🔄 Comparaison
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant={selectedPhotoMode ? 'contained' : 'outlined'}
              size={isMobile ? 'medium' : 'small'}
              startIcon={<CompareArrowsIcon />}
              onClick={togglePhotoSelectionMode}
              sx={{ fontSize: { xs: '0.875rem', md: '0.8125rem' } }}
            >
              {selectedPhotoMode ? 'Sélection' : 'Photos'}
            </Button>
            <IconButton onClick={onClose} size={isMobile ? 'medium' : 'small'}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
        {/* Trend Indicator */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            {getTrendIcon()}
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Évolution de l'état
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            Comparer les deux états des lieux pour identifier les changements
          </Typography>
        </Box>

        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        {/* Selected Photos Comparison View */}
        {selectedPhotoMode && (selectedPhotos1.length > 0 || selectedPhotos2.length > 0) && (
          <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'inherit' }}>
                📸 Comparaison des photos sélectionnées
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Selected Photos from Assessment 1 */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'inherit' }}>
                    État précédent ({selectedPhotos1.length} sélectionnée{selectedPhotos1.length > 1 ? 's' : ''})
                  </Typography>
                  <ImageList cols={isMobile ? 1 : 2} gap={isMobile ? 4 : 8}>
                    {selectedPhotos1.map(photoIndex => {
                      const photo = assessment1.photos?.[photoIndex];
                      if (!photo) return null;
                      return (
                        <ImageListItem key={photoIndex}>
                          <img
                            src={photo.file_url || `/storage/vehicle-assessments/${photo.file_path}`}
                            alt={photo.description || `Photo ${photoIndex + 1}`}
                            style={{
                              width: '100%',
                              height: isMobile ? 250 : 150,
                              objectFit: 'cover',
                              borderRadius: 4,
                              cursor: 'pointer'
                            }}
                            onClick={() => handleImageClick(photo.file_url || '')}
                          />
                          {photo.description && (
                            <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: 'inherit' }}>
                              {photo.description}
                            </Box>
                          )}
                        </ImageListItem>
                      );
                    })}
                  </ImageList>
                </Box>

                {/* Selected Photos from Assessment 2 */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'inherit' }}>
                    État actuel ({selectedPhotos2.length} sélectionnée{selectedPhotos2.length > 1 ? 's' : ''})
                  </Typography>
                  <ImageList cols={isMobile ? 1 : 2} gap={isMobile ? 4 : 8}>
                    {selectedPhotos2.map(photoIndex => {
                      const photo = assessment2.photos?.[photoIndex];
                      if (!photo) return null;
                      return (
                        <ImageListItem key={photoIndex}>
                          <img
                            src={photo.file_url || `/storage/vehicle-assessments/${photo.file_path}`}
                            alt={photo.description || `Photo ${photoIndex + 1}`}
                            style={{
                              width: '100%',
                              height: isMobile ? 250 : 150,
                              objectFit: 'cover',
                              borderRadius: 4,
                              cursor: 'pointer'
                            }}
                            onClick={() => handleImageClick(photo.file_url || '')}
                          />
                          {photo.description && (
                            <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: 'inherit' }}>
                              {photo.description}
                            </Box>
                          )}
                        </ImageListItem>
                      );
                    })}
                  </ImageList>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Copy Descriptions Button */}
        {onEdit && (
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: copyError ? 2 : 0 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ArrowForwardIcon />}
                endIcon={<ContentCopyIcon />}
                onClick={handleCopyDescriptions}
                size={isMobile ? 'large' : 'medium'}
                sx={{
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.5, md: 1 },
                  fontSize: { xs: '0.875rem', md: '0.9375rem' },
                  fontWeight: 600
                }}
                title="Copie les descriptions de photos et les notes de l'évaluation précédente vers l'évaluation actuelle"
              >
                Copier les descriptions (État précédent → État actuel)
              </Button>
            </Box>
            {copyError && (
              <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setCopyError(null)}>
                {copyError}
              </Alert>
            )}
          </Box>
        )}

        {/* Side by Side Comparison */}
        <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 }, flexDirection: { xs: 'column', md: 'row' } }}>
          {renderAssessmentCard(assessment1, 'État précédent')}
          {renderAssessmentCard(assessment2, 'État actuel')}
        </Box>

        {/* Comparison Table */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Détails de comparaison
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(assessment1.status)}
                      size="small"
                      sx={{ backgroundColor: getStatusColor(assessment1.status), color: 'white' }}
                    />
                  </TableCell>
                  <TableCell align="center">{getTrendIcon()}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(assessment2.status)}
                      size="small"
                      sx={{ backgroundColor: getStatusColor(assessment2.status), color: 'white' }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Photos</TableCell>
                  <TableCell>{assessment1.photos?.length || 0}</TableCell>
                  <TableCell align="center">
                    {(assessment2.photos?.length || 0) > (assessment1.photos?.length || 0) ? '📈' : 
                     (assessment2.photos?.length || 0) < (assessment1.photos?.length || 0) ? '📉' : '➡️'}
                  </TableCell>
                  <TableCell>{assessment2.photos?.length || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                  <TableCell>{assessment1.notes?.length || 0}</TableCell>
                  <TableCell align="center">
                    {(assessment2.notes?.length || 0) > (assessment1.notes?.length || 0) ? '📈' : 
                     (assessment2.notes?.length || 0) < (assessment1.notes?.length || 0) ? '📉' : '➡️'}
                  </TableCell>
                  <TableCell>{assessment2.notes?.length || 0}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          fullWidth={isMobile}
          size={isMobile ? 'large' : 'medium'}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>

    {/* Lightbox for Full-Size Images */}
    <Dialog
      open={lightboxOpen}
      onClose={() => setLightboxOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          boxShadow: 'none'
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '70vh'
          }}
        >
          <img
            src={lightboxImage}
            alt="Full size"
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain'
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>

    {/* Success Snackbar */}
    <Snackbar
      open={copySuccess}
      autoHideDuration={3000}
      onClose={() => setCopySuccess(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        onClose={() => setCopySuccess(false)} 
        severity="success" 
        sx={{ width: '100%' }}
      >
        Descriptions et notes copiées avec succès ! Ouverture du mode édition...
      </Alert>
    </Snackbar>
    </>
  );
};

export default CompareAssessmentsModal;

